import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Check, ArrowRight, Crown, Users, Sparkles, ShieldCheck, X } from 'lucide-react';
import axios from 'axios';

import { useTranslation } from 'react-i18next';

const API = process.env.REACT_APP_BACKEND_URL;

/* Pricing page — audit D4.1, D4.2, D4.3, D4.4, FIT.
   Surfaces all three tiers with named prices, a comparison table, value
   anchoring against the $400+ desktop-software category, trust marks,
   refund policy, and CTAs. Titles/meta driven by PricingPageHead. */

const TIERS = [
  {
    id: 'free',
    name: 'Free',
    price: '$0',
    cadence: 'forever',
    tagline: 'For daily reading and light study.',
    features: [
      'Ask Eili questions in real time',
      'Read the Bible in 12+ translations',
      'Save conversations and reflections',
      'Weekly recaps of what you studied',
      'Community study feed',
    ],
    cta: 'Start free — no credit card',
    ctaTestId: 'pricing-free-cta',
    highlight: false,
  },
  {
    id: 'premium',
    name: 'Premium',
    price: '$9.99',
    cadence: 'per month',
    tagline: 'For serious study — pastors, teachers, and lifelong students.',
    features: [
      'Everything in Free',
      'Full commentary depth from vetted sources',
      'Greek & Hebrew original-language roots',
      'Cross-reference tools across the whole canon',
      'Deep Study Mode for sermon and lesson prep',
      'Priority answers, no daily limits',
    ],
    cta: 'Try Premium — 7 days free',
    ctaTestId: 'pricing-premium-cta',
    highlight: true,
    badge: 'Most popular',
  },
  {
    id: 'share',
    name: 'Share',
    price: '$19.99',
    cadence: 'per month',
    tagline: 'For families, small groups, and study partners.',
    features: [
      'Everything in Premium for the plan owner',
      'Invite up to 4 family members or friends',
      'Every member gets full Premium access',
      'Up to 3 devices per member',
      'Shared study feed and reactions',
    ],
    cta: 'Start a Share plan',
    ctaTestId: 'pricing-share-cta',
    highlight: false,
  },
];

/* Comparison rows — quick scan for the buyer who is weighing tiers.
   Kept short: 8 rows keeps the table readable on a single mobile screen. */
const COMPARE_ROWS = [
  { label: 'Ask questions and get grounded answers', free: true, premium: true, share: true },
  { label: 'Read the Bible in 12+ translations', free: true, premium: true, share: true },
  { label: 'Save conversations and reflections', free: true, premium: true, share: true },
  { label: 'Full commentary depth', free: false, premium: true, share: true },
  { label: 'Greek & Hebrew original-language roots', free: false, premium: true, share: true },
  { label: 'Cross-reference tools', free: false, premium: true, share: true },
  { label: 'Deep Study Mode (sermon + lesson prep)', free: false, premium: true, share: true },
  { label: 'Share with up to 4 people', free: false, premium: false, share: true },
];

export default function PricingPage() {
  const {
    t
  } = useTranslation();

  const navigate = useNavigate();
  const [trialEnabled, setTrialEnabled] = useState(false);

  useEffect(() => {
    // Fetch trial config so Premium CTA copy matches the current promotion.
    axios.get(`${API}/api/guest/trial-config`).catch(() => {}).then((res) => {
      if (res?.data?.enabled) setTrialEnabled(true);
    });
    // Per-page title (audit D5.1).
    const prevTitle = document.title;
    document.title = 'Pricing — Free, Premium, and Share Plans · Eili';
    const meta = document.querySelector('meta[name="description"]');
    const prevDesc = meta ? meta.getAttribute('content') : null;
    if (meta) meta.setAttribute('content', 'Eili pricing: Free forever, Premium at $9.99/mo, Share at $19.99/mo for up to 5 people. Scholar-grade Bible study depth without the $400+ desktop price tag. 7-day free trial, no credit card required.');

    // Product/Offer JSON-LD schema (audit D5.5) unlocks Google Shopping /
    // Merchant rich results and reinforces search intent on
    // "Bible study app pricing".
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = 'eili-pricing-jsonld';
    script.textContent = JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Product',
      name: t('generated.pricingpage_eili_bible_study_ad02'),
      description: t('generated.pricingpage_a_bible_study_5f19'),
      url: 'https://askeili.com/pricing',
      brand: { '@type': 'Organization', name: t('generated.pricingpage_eili_9cce') },
      offers: [
        { '@type': 'Offer', name: t('generated.pricingpage_free_57bb'), price: '0', priceCurrency: 'USD', description: t('generated.pricingpage_free_forever_for_0431') },
        { '@type': 'Offer', name: t('generated.pricingpage_premium_fb52'), price: '9.99', priceCurrency: 'USD', description: t('generated.pricingpage_full_commentary_depth_40a9') },
        { '@type': 'Offer', name: t('generated.pricingpage_share_5979'), price: '19.99', priceCurrency: 'USD', description: t('generated.pricingpage_everything_in_premium_fb78') },
      ],
    });
    document.head.appendChild(script);

    return () => {
      document.title = prevTitle;
      if (meta && prevDesc) meta.setAttribute('content', prevDesc);
      const existing = document.getElementById('eili-pricing-jsonld');
      if (existing) existing.remove();
    };
  }, []);

  const handleTierCta = (tierId) => {
    if (tierId === 'free') {
      navigate('/signup');
    } else if (tierId === 'premium') {
      navigate(trialEnabled ? '/signup?trial=true&src=pricing' : '/signup');
    } else if (tierId === 'share') {
      navigate('/signup');
    }
  };

  return (
    <main className="page-container relative overflow-x-hidden" style={{ background: 'var(--theme-background)' }} role="main" data-testid="pricing-page">
      {/* `min-w-0` is critical — .page-container is a flex column, so
          without it the .page-content grows to accommodate any child's
          intrinsic width (e.g. the compare table's min-w-[560px]) and
          pushes the whole page's H1/subhead off the right edge on
          mobile. `w-full` clamps the content to the viewport width. */}
      <div className="page-content w-full min-w-0 max-w-6xl mx-auto px-4 py-8 sm:py-10">
        {/* Simple nav — keeps consistent with landing */}
        <nav className="flex items-center justify-between mb-10" aria-label={t('generated.pricingpage_primary_f1a4')}>
          <a href="/" className="flex items-center gap-2" aria-label={t('generated.pricingpage_eili_home_acad')}>
            <img src="/logo.png" alt={t('generated.pricingpage_eili_bible_study_ad02')} className="h-8 w-8" />
            <span className="text-lg font-semibold" style={{ color: 'var(--theme-text)', fontFamily: "'Playwrite ES Deco', cursive" }}>{t('generated.pricingpage_eili_9cce')}</span>
          </a>
          <div className="flex items-center gap-1">
            <button onClick={() => navigate('/faq')} className="px-3 py-2 rounded-full text-sm font-medium" style={{ color: 'var(--theme-text)' }} data-testid="pricing-nav-faq">{t('generated.pricingpage_faq_e8f5')}</button>
            <button onClick={() => navigate('/login')} className="px-3 py-2 rounded-full text-sm font-medium" style={{ color: 'var(--theme-text)' }} data-testid="pricing-nav-signin">{t('generated.pricingpage_sign_in_9edf')}</button>
          </div>
        </nav>

        {/* Header */}
        <header className="text-center mb-10">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold mb-3 leading-tight" style={{ color: 'var(--theme-text)' }} data-testid="pricing-h1">
            {t('generated.pricingpage_pricing_that_respects_506d')}
          </h1>
          <p className="text-lg max-w-2xl mx-auto mb-4" style={{ color: 'var(--theme-textSecondary)' }} data-testid="pricing-subhead">
            {t('generated.pricingpage_free_forever_for_3dcf')}
          </p>
          <div className="flex justify-center">
            <p className="inline-flex items-start gap-2 text-sm font-medium rounded-2xl px-4 py-2 max-w-fit" style={{ background: 'rgba(16,185,129,0.12)', border: '1px solid rgba(16,185,129,0.25)', color: 'var(--theme-text)' }} data-testid="pricing-value-anchor">
              <ShieldCheck className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#10b981' }} />
              <span>{t('generated.pricingpage_scholar_grade_depth_a8a7')}</span>
            </p>
          </div>
        </header>

        {/* Tier cards — extra top padding on the grid so the "Most Popular"
            badge (positioned -top-3) doesn't clip when tiers stack on mobile. */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12 pt-3" aria-label={t('generated.pricingpage_plans_0ef1')} data-testid="pricing-tiers">
          {TIERS.map((tier) => (
            <article
              key={tier.id}
              className={`relative rounded-2xl p-6 flex flex-col ${tier.highlight ? 'shadow-2xl' : ''}`}
              style={{
                background: 'var(--glass-bg)',
                border: tier.highlight ? '2px solid var(--theme-accent)' : '1px solid var(--glass-border)',
              }}
              data-testid={`pricing-tier-${tier.id}`}
            >
              {tier.badge && (
                <span
                  className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide text-white"
                  style={{ background: 'var(--theme-accent)' }}
                >
                  {tier.badge}
                </span>
              )}
              <div className="flex items-center gap-2 mb-2">
                {tier.id === 'premium' && <Crown className="h-5 w-5" style={{ color: '#eab308' }} />}
                {tier.id === 'share' && <Users className="h-5 w-5" style={{ color: 'var(--theme-accent)' }} />}
                {tier.id === 'free' && <Sparkles className="h-5 w-5" style={{ color: 'var(--theme-textSecondary)' }} />}
                <h2 className="text-2xl font-bold" style={{ color: 'var(--theme-text)' }}>{tier.name}</h2>
              </div>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-4xl font-bold" style={{ color: 'var(--theme-text)' }}>{tier.price}</span>
                <span className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>{tier.cadence}</span>
              </div>
              <p className="text-sm mb-5" style={{ color: 'var(--theme-textSecondary)' }}>{tier.tagline}</p>
              <ul className="space-y-2 mb-6 flex-1">
                {tier.features.map((f, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm" style={{ color: 'var(--theme-text)' }}>
                    <Check className="h-4 w-4 mt-0.5 shrink-0" style={{ color: '#10b981' }} />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => handleTierCta(tier.id)}
                className="w-full rounded-xl py-5 font-medium"
                style={tier.highlight
                  ? { background: `linear-gradient(135deg, var(--theme-accent) 0%, var(--theme-accentLight) 100%)`, color: 'white' }
                  : { background: 'transparent', color: 'var(--theme-text)', border: '1px solid var(--glass-border)' }
                }
                data-testid={tier.ctaTestId}
              >
                {tier.cta}
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </article>
          ))}
        </section>

        {/* Trust row */}
        <div className="text-center mb-12">
          <p className="text-sm inline-flex items-center gap-1.5" style={{ color: 'var(--theme-text)' }} data-testid="pricing-trust-line">
            <ShieldCheck className="h-4 w-4" style={{ color: '#10b981' }} />
            <span><strong>{t('generated.pricingpage_no_credit_card_da37')}</strong> {t('generated.pricingpage_to_start_cancel_1577')}</span>
          </p>
          <p className="text-xs mt-2" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('generated.pricingpage_billed_monthly_in_3de5')}
          </p>
        </div>

        {/* Comparison table */}
        <section className="max-w-4xl mx-auto min-w-0 mb-12" aria-label={t('generated.pricingpage_feature_comparison_666c')} data-testid="pricing-compare-section">
          <h2 className="text-2xl font-bold mb-4 text-center" style={{ color: 'var(--theme-text)' }}>{t('generated.pricingpage_compare_plans_ceae')}</h2>
          <div className="overflow-x-auto min-w-0 rounded-2xl" style={{ background: 'var(--glass-bg)', border: '1px solid var(--glass-border)' }}>
            <table className="w-full text-sm min-w-[560px]" data-testid="pricing-compare-table">
              <thead>
                <tr style={{ borderBottom: '1px solid var(--glass-border)' }}>
                  <th className="text-left px-4 py-3 font-semibold" style={{ color: 'var(--theme-text)' }}>{t('generated.pricingpage_feature_8d43')}</th>
                  <th className="px-4 py-3 font-semibold" style={{ color: 'var(--theme-text)' }}>{t('generated.pricingpage_free_57bb')}</th>
                  <th className="px-4 py-3 font-semibold" style={{ color: 'var(--theme-text)' }}>{t('generated.pricingpage_premium_fb52')}</th>
                  <th className="px-4 py-3 font-semibold" style={{ color: 'var(--theme-text)' }}>{t('generated.pricingpage_share_5979')}</th>
                </tr>
              </thead>
              <tbody>
                {COMPARE_ROWS.map((row, i) => (
                  <tr key={i} style={{ borderBottom: i === COMPARE_ROWS.length - 1 ? 'none' : '1px solid var(--glass-border)' }}>
                    <td className="text-left px-4 py-3" style={{ color: 'var(--theme-text)' }}>{row.label}</td>
                    <td className="text-center px-4 py-3">{row.free ? <Check className="h-4 w-4 inline" style={{ color: '#10b981' }} /> : <X className="h-4 w-4 inline opacity-30" />}</td>
                    <td className="text-center px-4 py-3">{row.premium ? <Check className="h-4 w-4 inline" style={{ color: '#10b981' }} /> : <X className="h-4 w-4 inline opacity-30" />}</td>
                    <td className="text-center px-4 py-3">{row.share ? <Check className="h-4 w-4 inline" style={{ color: '#10b981' }} /> : <X className="h-4 w-4 inline opacity-30" />}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* FAQ mini-section */}
        <section className="max-w-2xl mx-auto text-center pb-10">
          <p className="text-sm" style={{ color: 'var(--theme-textSecondary)' }}>
            {t('generated.pricingpage_still_have_questions_3c9b')} <button onClick={() => navigate('/faq')} className="underline font-medium" style={{ color: 'var(--theme-accent)' }} data-testid="pricing-faq-link">{t('generated.pricingpage_read_the_faq_e958')}</button>
          </p>
        </section>
      </div>
    </main>
  );
}
